import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { Staff } from '@/services/school.api';
import { OrgStaffApi } from '@/services/org-staff.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';

const ROLES = ['admin', 'manager', 'teacher', 'staff', 'cashier'] as const;
const STATUSES = ['active', 'on_leave', 'resigned', 'terminated'] as const;

const statusTone: Record<Staff['status'], 'green' | 'amber' | 'slate' | 'red'> = {
  active: 'green',
  on_leave: 'amber',
  resigned: 'slate',
  terminated: 'red',
};

const schema = z.object({
  name: z.string().min(1, 'Required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  role: z.enum(ROLES),
  employeeId: z.string().min(1, 'Required'),
  designation: z.string().min(1, 'Required'),
  department: z.string().optional().or(z.literal('')),
  qualification: z.string().optional().or(z.literal('')),
  joiningDate: z.string().min(1, 'Required'),
  salary: z.coerce.number().min(0).optional(),
  mobile: z.string().optional().or(z.literal('')),
  status: z.enum(STATUSES),
});
type FormValues = z.infer<typeof schema>;

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Org tier — Staff, cross-school. Mirrors the school-tier StaffPage's core CRUD (name/role/employment fields) scoped to a picked school — bank/PAN/Aadhar/address/photo stay school-tier-only detail-page edits, not duplicated here. */
export function OrgStaffPage() {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    setPage(1);
  }, [search, limit, schoolId]);

  const { data: schools = [] } = useQuery({ queryKey: ['org-staff-schools'], queryFn: () => OrgStaffApi.schools() });

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['org-staff', schoolId, page, limit, search],
    queryFn: () => OrgStaffApi.list({ schoolId, page, limit, search: search || undefined }),
    enabled: !!schoolId,
    placeholderData: keepPreviousData,
  });
  const staff = pageData?.items ?? [];

  const [modal, setModal] = useState<{ open: boolean; staff?: Staff }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; staff?: Staff }>({ open: false });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-staff', schoolId] });

  const upsert = useMutation({
    mutationFn: (v: { id?: string; payload: Record<string, unknown> }) =>
      v.id ? OrgStaffApi.update(v.id, { ...v.payload, schoolId }) : OrgStaffApi.create({ ...v.payload, schoolId }),
    onSuccess: () => {
      invalidate();
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => OrgStaffApi.remove(id, schoolId),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  return (
    <>
      <PageHeader title="Staff" description="Staff records across your organization's schools. Pick a school to manage its staff." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-64" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Search name, employee id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!schoolId}
            className="w-full rounded-md border border-slate-200 py-1.5 pl-9 pr-3 text-sm disabled:bg-slate-50"
          />
        </div>
        <div className="flex-1" />
        <button className="btn-primary" onClick={() => setModal({ open: true })} disabled={!schoolId} title={!schoolId ? 'Select a school first' : 'Add staff'}>
          <Plus className="mr-1.5 h-4 w-4" /> Add staff
        </button>
      </div>

      {!schoolId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a school to view its staff.</div>
      ) : (
        <>
          <DataTable<Staff>
            rows={staff}
            getRowId={(r) => r.id}
            isLoading={isLoading}
            emptyMessage="No staff added yet."
            columns={[
              {
                key: 'no',
                header: 'No.',
                width: '1%',
                render: (_s, index) => <span className="text-slate-400">{(page - 1) * limit + index + 1}</span>,
              },
              {
                key: 'name',
                header: 'Name',
                render: (s) => (
                  <div className="leading-tight">
                    <div className="font-medium text-slate-900">{s.user?.name ?? '(unnamed)'}</div>
                    <div className="text-xs text-slate-500">{s.employeeId}</div>
                  </div>
                ),
              },
              { key: 'designation', header: 'Designation', render: (s) => s.designation },
              { key: 'department', header: 'Department', render: (s) => s.department ?? <span className="text-slate-400">—</span> },
              { key: 'role', header: 'Role', render: (s) => <Badge tone="blue">{s.user?.role ?? '—'}</Badge> },
              { key: 'mobile', header: 'Mobile', render: (s) => s.mobile ?? <span className="text-slate-400">—</span> },
              { key: 'status', header: 'Status', render: (s) => <Badge tone={statusTone[s.status]}>{s.status.replace('_', ' ')}</Badge> },
            ]}
            actions={(s) => (
              <>
                <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600" onClick={() => setModal({ open: true, staff: s })} title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setConfirm({ open: true, staff: s })} title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          />

          {pageData && (
            <Pagination page={pageData.page} totalPages={pageData.totalPages} total={pageData.total} limit={pageData.limit} onPageChange={setPage} onLimitChange={setLimit} />
          )}
        </>
      )}

      <StaffFormModal
        open={modal.open}
        staff={modal.staff}
        saving={upsert.isPending}
        errorMsg={errMsg(upsert.error)}
        onClose={() => setModal({ open: false })}
        onSubmit={(v) => {
          const payload: Record<string, unknown> = {
            name: v.name,
            email: v.email || undefined,
            role: v.role,
            employeeId: v.employeeId,
            designation: v.designation,
            department: v.department || undefined,
            qualification: v.qualification || undefined,
            joiningDate: v.joiningDate,
            salary: typeof v.salary === 'number' ? v.salary : undefined,
            mobile: v.mobile || undefined,
            status: v.status,
          };
          upsert.mutate({ id: modal.staff?.id, payload });
        }}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.staff && remove.mutate(confirm.staff.id)}
        loading={remove.isPending}
        title="Delete staff record?"
        message={`Remove ${confirm.staff?.user?.name ?? 'this staff member'} (${confirm.staff?.employeeId}). This cannot be undone.`}
        confirmText="Delete"
      />
    </>
  );
}

function StaffFormModal({
  open,
  staff,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  staff?: Staff;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const isEdit = !!staff;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: staff?.user?.name ?? '',
      email: staff?.user?.email ?? '',
      role: (staff?.user?.role as (typeof ROLES)[number]) ?? 'teacher',
      employeeId: staff?.employeeId ?? '',
      designation: staff?.designation ?? '',
      department: staff?.department ?? '',
      qualification: staff?.qualification ?? '',
      joiningDate: staff?.joiningDate?.slice(0, 10) ?? '',
      salary: staff?.salary ?? undefined,
      mobile: staff?.mobile ?? '',
      status: staff?.status ?? 'active',
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={staff ? `Edit ${staff.user?.name ?? 'staff'}` : 'Add staff'}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : staff ? 'Save changes' : 'Add staff'}
          </button>
        </>
      }
    >
      <form className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Full name" required error={errors.name?.message}>
          <Input {...register('name')} />
        </Field>
        <Field label="Role" required>
          <Select {...register('role')}>
            {ROLES.map((r) => (
              <option key={r} value={r} className="capitalize">
                {r}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" {...register('email')} />
        </Field>
        <Field label="Mobile">
          <Input {...register('mobile')} placeholder="9876543210" />
        </Field>
        <Field label="Employee ID" required error={errors.employeeId?.message}>
          <Input {...register('employeeId')} disabled={isEdit} />
        </Field>
        <Field label="Designation" required error={errors.designation?.message}>
          <Input {...register('designation')} />
        </Field>
        <Field label="Department">
          <Input {...register('department')} />
        </Field>
        <Field label="Qualification">
          <Input {...register('qualification')} />
        </Field>
        <Field label="Joining date" required error={errors.joiningDate?.message}>
          <Input type="date" {...register('joiningDate')} />
        </Field>
        <Field label="Salary (₹/mo)">
          <Input type="number" {...register('salary')} />
        </Field>
        <Field label="Status" required>
          <Select {...register('status')}>
            {STATUSES.map((s) => (
              <option key={s} value={s} className="capitalize">
                {s.replace('_', ' ')}
              </option>
            ))}
          </Select>
        </Field>

        {errorMsg && <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}
